import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, withRouter } from "react-router-dom";
import Plus from "../../../assets/svg/icons/common/Plus";
import ButtonIcon from "../../../components/ButtonIcon/ButtonIcon";
import SearchInput from "../../../components/SearchInput/SearchInput";
import Table from "../../../components/Table";
import useCommonStore from "../../../stores/common";
import useTicketStore from "../../../stores/ticket";
import useUserStore from "../../../stores/user";
import { fetchTickets } from "../../../services/ticket";
import Pagination from "../../../components/Pagination/Pagination";
import FilterDrawer from "../components/filter-drawer";
import { CreateTicketModal } from "../components/create-ticket-modal";
import Avatar from "../../../components/Avatar/Avatar";
import Filter from "../../../assets/svg/icons/common/Filter";
import RedDotBadge from "../../../assets/svg/icons/common/RedDotBadge";
import styles from "./styles.module.scss";
import pageHeadingStyles from "../../../styles/PageHeader.module.scss";
import TicketStatusIcon from "../components/TicketStatusIcon";
import {
  getTicketStatusIconType,
  getTicketStatusSlug,
} from "../ticketStatusUtils";

const List = ({ location }) => {
  const userLoading = useUserStore((state) => state.userLoading);
  const isEntitySuperAdmin = useUserStore((state) => state.isEntitySuperAdmin);
  const isGodUser = useUserStore((state) => state.isGodUser);
  const isAdmin = useUserStore(state => state.isAdmin);
  const canModifyTicket = isEntitySuperAdmin || isGodUser || isAdmin;
  const [ticketDataLoading, setTicketDataLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Ticket store states
  const tickets = useTicketStore((state) => state.tickets);
  const selectedTicket = useTicketStore((state) => state.selectedTicket);
  const setSelectedTicket = useTicketStore((state) => state.setSelectedTicket);
  const searchQuery = useTicketStore((state) => state.searchQuery);
  const setSearchQuery = useTicketStore((state) => state.setSearchQuery);
  const pageCount = useTicketStore((state) => state.pageCount);
  const currentPage = useTicketStore((state) => state.currentPage);
  const setCurrentPage = useTicketStore((state) => state.setCurrentPage);
  const showDetails = useCommonStore((state) => state.showDetails);
  const setShowDetails = useCommonStore((state) => state.setShowDetails);
  const setFilterDrawerOpen = useCommonStore(
    (state) => state.setFilterDrawerOpen,
  );
  const filters = useTicketStore((state) => state.ticketFilterSearchQuery);
  const totalItems = useTicketStore((state) => state.totalItems);
  const itemsPerPage = useTicketStore((state) => state.itemsPerPage);
  const merchantTicketScopePowerappId = useTicketStore(
    (state) => state.merchantTicketScopePowerappId,
  );
  const merchantTicketScopeDba = useTicketStore(
    (state) => state.merchantTicketScopeDba,
  );
  const ticketsBasePath = location.pathname.startsWith("/v2/")
    ? "/v2/service-tickets"
    : "/service-tickets";

  const getActiveFiltersCount = () => {
    if (!filters) return 0;
    const dbaCount = filters.dba?.length ?? 0;
    const hasProblemType = Boolean(filters.problemType);
    const hasOwnerGroup = Boolean(filters.ownerGroup);
    const hasDateRange = Boolean(filters.created?.from || filters.created?.to);
    const statusCount = filters.status?.length ?? 0;

    return [
      dbaCount,
      hasProblemType,
      hasOwnerGroup,
      hasDateRange,
      statusCount,
    ].reduce((sum, val) => {
      if (typeof val === "number") {
        return sum + val;
      }
      return sum + (val ? 1 : 0);
    }, 0);
  };

  const activeFiltersCount = getActiveFiltersCount();

  const onRowClick = useCallback(
    (row) => {
      if (!selectedTicket || selectedTicket.key !== row.key) {
        setSelectedTicket(row);
        setShowDetails(true);
      } else {
        setShowDetails(true);
      }
    },
    [selectedTicket, setSelectedTicket, setShowDetails],
  );

  const { ticketFilterSearchQuery, setTicketFilterSearchQuery } =
    useTicketStore((state) => ({
      ticketFilterSearchQuery: state.ticketFilterSearchQuery,
      setTicketFilterSearchQuery: state.setTicketFilterSearchQuery,
    }));

  const columns = useMemo(
    () => [
      {
        headerName: "TICKET #",
        field: "id",
        width: "120px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "id",
        sortColumn: ticketFilterSearchQuery.sortValue,
      },
      {
        headerName: "STATUS",
        field: "status",
        width: "120px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "status",
        sortColumn: ticketFilterSearchQuery.sortValue,
        renderCell: (value) => {
          const slug = getTicketStatusSlug(value);
          const iconType = getTicketStatusIconType(value);

          return (
            <div
              className={`${styles.statusCell} ${styles[`status_${slug}`]}`}
            >
              <TicketStatusIcon type={iconType} className={styles.statusIcon} />
              <span>{value}</span>
            </div>
          );
        },
        hideCell: showDetails,
      },
      {
        headerName: "DBA",
        field: "dba",
        width: "135px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "dba",
        sortColumn: ticketFilterSearchQuery.sortValue,
      },
      {
        headerName: "MID",
        field: "mid",
        width: "135px",
        allowSort: false,
        hideCell: showDetails,
        renderCell: (value) =>
          value ? `...${value?.toString()?.slice(-6)}` : "-",
      },
      {
        headerName: "PROCESSOR",
        field: "processor",
        width: "135px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "processor",
        sortColumn: ticketFilterSearchQuery.sortValue,
        renderCell: (value, row) => (
          <div className={styles.processorCell}>
            {row.processorLogo && (
              <div className={styles.processorLogo}>
                <img src={row.processorLogo} alt={value || ""} />
              </div>
            )}
            <span>{value?.toUpperCase() || "-"}</span>
          </div>
        ),
      },
      {
        headerName: "PROBLEM TYPE",
        field: "problem_type",
        width: "135px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "problem_type",
        sortColumn: ticketFilterSearchQuery.sortValue,
        hideCell: showDetails,
      },
      {
        headerName: "CREATED",
        field: "created_at",
        width: "100px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "created_at",
        sortColumn: ticketFilterSearchQuery.sortValue,
        hideCell: showDetails,
      },
      {
        headerName: "LAST COMMENT",
        field: "last_comment",
        width: "115px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "last_comment",
        sortColumn: ticketFilterSearchQuery.sortValue,
        hideCell: showDetails,
      },
      {
        headerName: "OWNER GROUP",
        field: "owner_group",
        width: "135px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "owner_group",
        sortColumn: ticketFilterSearchQuery.sortValue,
        hideCell: showDetails,
      },
      {
        headerName: "REP",
        field: "assigned_to",
        width: "120px",
        allowSort: true,
        sortBy: ticketFilterSearchQuery.sortBy === "assigned_to",
        sortColumn: ticketFilterSearchQuery.sortValue,
        hideCell: showDetails,
        renderCell: (value) => (
          <div className={styles.repCell}>
            <Avatar name={value?.charAt(0) ?? "-"} size="xs" />
            <span>{value ?? "-"}</span>
          </div>
        ),
      },
    ],
    [showDetails],
  );

  const handleSort = useCallback(
    (field) => {
      setTicketFilterSearchQuery({
        sortBy: field,
        sortValue:
          ticketFilterSearchQuery.sortValue === "DESC" ? "ASC" : "DESC",
      });
    },
    [ticketFilterSearchQuery.sortValue, setTicketFilterSearchQuery],
  );

  const loadTickets = useCallback(async () => {
    try {
      setTicketDataLoading(true);
      await fetchTickets({
        sortBy: ticketFilterSearchQuery.sortBy,
        sortValue: ticketFilterSearchQuery.sortValue,
      });
    } catch (error) {
      console.error("Error loading tickets:", error);
    } finally {
      setTicketDataLoading(false);
    }
  }, [ticketFilterSearchQuery.sortBy, ticketFilterSearchQuery.sortValue]);

  useEffect(() => {
    loadTickets();
  }, [
    currentPage,
    searchQuery,
    filters.status,
    filters.problemType,
    filters.ownerGroup,
    filters.dba,
    filters.created.from,
    filters.created.to,
    ticketFilterSearchQuery.sortBy,
    ticketFilterSearchQuery.sortValue,
    merchantTicketScopePowerappId,
    loadTickets,
  ]);

  const onChangeSearchInput = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  const tableLoading = userLoading || ticketDataLoading;

  return (
    <>
      <div
        className={`${pageHeadingStyles.page_heading} ${pageHeadingStyles.column}`}
      >
        <div className={styles.page_heading}>
          <div className={pageHeadingStyles.title_contents}>
            <p>Service Tickets</p>
            {merchantTicketScopePowerappId != null && (
              <div className={styles.scope_meta}>
                <span className={styles.scope_subtitle}>
                  {merchantTicketScopeDba
                    ? `For ${merchantTicketScopeDba}`
                    : "For this merchant"}
                </span>
                <Link className={styles.all_tickets_link} to={ticketsBasePath}>
                  See All Tickets
                </Link>
              </div>
            )}
          </div>
          {!showDetails && canModifyTicket && (
            <div className={styles.right_side}>
              <ButtonIcon
                bgColor="#4C70EF"
                textColor="#fff"
                onClick={() => setCreateModalOpen(true)}
              >
                <Plus />
                <span>Create Ticket</span>
              </ButtonIcon>
            </div>
          )}
        </div>
        <div className={styles.filterContainer}>
          <SearchInput
            onChange={onChangeSearchInput}
            searchValue={searchQuery}
          />
          <div className={styles.filterIconContainer}>
            <ButtonIcon
              bgColor="#EDEDF4"
              onClick={() => setFilterDrawerOpen(true)}
              className={styles.filter_button}
            >
              <Filter />
              {activeFiltersCount > 0 && <RedDotBadge />}
              Filter
              {activeFiltersCount > 0 ? `: ${activeFiltersCount}` : ""}
            </ButtonIcon>
          </div>
        </div>
      </div>

      <div className={styles.card}>
        <Table
          layout="fixed"
          noMarginTop
          columns={columns}
          data={tickets || []}
          onRowClick={onRowClick}
          onSortClick={handleSort}
          selectedRowId={selectedTicket?.key}
          className={styles.ticket_table}
          sortBy={ticketFilterSearchQuery.sortBy}
          sortColumn={ticketFilterSearchQuery.sortValue}
          enableSkeleton
          dataLoading={tableLoading}
          emptyMessage="No tickets found"
        />

        {!tableLoading && tickets?.length > 0 && (
          <div className={styles.pagination_container}>
            <Pagination
              pageCount={pageCount}
              currentPage={currentPage - 1}
              onPageChange={({ selected }) => {
                setCurrentPage(selected + 1);
              }}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
            />
          </div>
        )}
      </div>
      <FilterDrawer />
      <CreateTicketModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
      />
    </>
  );
};

export default withRouter(List);
